import React from 'react';
import { useSelector } from 'react-redux';
import {
  HiOutlineInformationCircle,
  HiOutlineBriefcase,
  HiOutlineDocumentText,
  HiOutlineChatBubbleLeftRight,
  HiOutlineChartBar,
  HiOutlineArrowTrendingUp,
  HiOutlineArrowTrendingDown,
  HiOutlineEnvelope,
  HiOutlineBolt,
  HiOutlineLink,
  HiOutlineCurrencyDollar,
  HiOutlineChatBubbleLeftEllipsis,
  HiOutlineArrowUpCircle,
  HiOutlineArrowPath,
  HiOutlineGift,
} from 'react-icons/hi2';
import { Tooltip } from 'antd';
import { IAnalytics } from '../../../data-access';
import { RootState } from '../../../store';
import './general-statistics.scss';

interface IGeneralStatisticsProps {
  stats: IAnalytics;
  isLoading?: boolean;
  prevStats?: IAnalytics;
}

interface IChange {
  percent: string;
  direction: 'up' | 'down' | 'neutral';
}

const baseClass = 'general-statistics';

const calcChange = (current: number, previous: number): IChange => {
  if (!previous || previous === 0) {
    if (current > 0) return { percent: '', direction: 'up' };
    return { percent: '', direction: 'neutral' };
  }
  const change = ((current - previous) / previous) * 100;
  if (Math.abs(change) < 0.1) return { percent: '0%', direction: 'neutral' };
  return {
    percent: Math.abs(change).toFixed(1) + '%',
    direction: change > 0 ? 'up' : 'down',
  };
};

interface ISmallChange {
  diff: string;
  direction: 'up' | 'down' | 'neutral';
}

const calcSmallChange = (current: number, previous: number): ISmallChange => {
  const diff = current - previous;
  if (diff === 0) return { diff: '', direction: 'neutral' };
  return {
    diff: (diff > 0 ? '+' : '') + (Number.isInteger(diff) ? diff : diff.toFixed(2)),
    direction: diff > 0 ? 'up' : 'down',
  };
};

const getReplyRateColor = (rate: number): string => {
  if (rate >= 10) return 'good';
  if (rate >= 5) return 'warning';
  return 'low';
};

const ChangeIndicator = ({ change }: { change: IChange }): JSX.Element | null => {
  if (change.direction === 'neutral') return null;
  return (
    <span className={`${baseClass}__change ${baseClass}__change--${change.direction}`}>
      {change.direction === 'up'
        ? <HiOutlineArrowTrendingUp size={16} />
        : <HiOutlineArrowTrendingDown size={16} />
      }
      {change.percent && <span>{change.percent}</span>}
    </span>
  );
};

const SmallChangeIndicator = ({ change }: { change: ISmallChange }): JSX.Element | null => {
  if (change.direction === 'neutral' || !change.diff) return null;
  return (
    <span className={`${baseClass}__sm-change ${baseClass}__sm-change--${change.direction}`}>
      {change.diff}
    </span>
  );
};

export const GeneralStatistics = ({ stats, isLoading = false, prevStats }: IGeneralStatisticsProps): JSX.Element => {
  const isPipedriveConnected = useSelector((state: RootState) => state.tools.is_pipedrive_connected);

  const replyRateNum = stats?.reply_rate ? Number(stats.reply_rate) : 0;
  const formattedReplyRate = replyRateNum ? replyRateNum.toFixed(2) : '0';
  const replyRateColor = getReplyRateColor(replyRateNum);

  const changes = prevStats ? {
    opened_jobs: calcChange(stats?.opened_jobs ?? 0, prevStats?.opened_jobs ?? 0),
    bids: calcChange(stats?.bids ?? 0, prevStats?.bids ?? 0),
    replies: calcChange(stats?.replies ?? 0, prevStats?.replies ?? 0),
    reply_rate: calcChange(replyRateNum, prevStats?.reply_rate ? Number(prevStats.reply_rate) : 0),
  } : null;

  const calculateReplyCost = (usedConnects = 0, replies = 0): string => {
    return replies > 0 ? (usedConnects / 300 * 45 / replies).toFixed(2) : '0';
  }

  const calculateAvgConnects = (usedConnects = 0, bids = 0): string => {
    return bids > 0 ? (usedConnects / bids).toFixed(2) : '0';
  }

  const smChanges = prevStats ? {
    invites: calcSmallChange(stats?.invites ?? 0, prevStats?.invites ?? 0),
    used_connects: calcSmallChange(stats?.used_connects ?? 0, prevStats?.used_connects ?? 0),
    avg_connects: calcSmallChange(
      Number(calculateAvgConnects(stats?.used_connects, stats?.bids)),
      Number(calculateAvgConnects(prevStats?.used_connects, prevStats?.bids)),
    ),
    cost_per_reply: calcSmallChange(
      Number(calculateReplyCost(stats?.used_connects, stats?.replies)),
      Number(calculateReplyCost(prevStats?.used_connects, prevStats?.replies)),
    ),
    direct_messages: calcSmallChange(stats?.direct_messages ?? 0, prevStats?.direct_messages ?? 0),
    boost_connects: calcSmallChange(stats?.boost_connects ?? 0, prevStats?.boost_connects ?? 0),
    sync_with_crms: calcSmallChange(stats?.sync_with_crms ?? 0, prevStats?.sync_with_crms ?? 0),
    offers_received: calcSmallChange(stats?.offers_received ?? 0, prevStats?.offers_received ?? 0),
  } : null;

  const Skeleton = () => (
    <div className={`${baseClass}__skeleton`}>
      <div className={`${baseClass}__hero`}>
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className={`${baseClass}__stat`}>
            <div className={`${baseClass}__shimmer ${baseClass}__shimmer--value`} />
            <div className={`${baseClass}__shimmer ${baseClass}__shimmer--label`} />
          </div>
        ))}
      </div>
      <div className={`${baseClass}__divider`} />
      <div className={`${baseClass}__secondary`}>
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`${baseClass}__stat--sm`}>
            <div className={`${baseClass}__shimmer ${baseClass}__shimmer--value-sm`} />
            <div className={`${baseClass}__shimmer ${baseClass}__shimmer--label-sm`} />
          </div>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <div className={baseClass}>
        <Skeleton />
      </div>
    );
  }

  return (
    <div className={baseClass}>
      <div className={`${baseClass}__hero`}>
        <div className={`${baseClass}__stat`}>
          <div className={`${baseClass}__value-row`}>
            <span className={`${baseClass}__value`}>{stats?.opened_jobs ?? 0}</span>
            {changes && <ChangeIndicator change={changes.opened_jobs} />}
          </div>
          <span className={`${baseClass}__label`}>
            <HiOutlineBriefcase size={16} />
            Jobs Viewed
          </span>
        </div>
        <div className={`${baseClass}__stat`}>
          <div className={`${baseClass}__value-row`}>
            <span className={`${baseClass}__value`}>{stats?.bids ?? 0}</span>
            {changes && <ChangeIndicator change={changes.bids} />}
          </div>
          <span className={`${baseClass}__label`}>
            <HiOutlineDocumentText size={16} />
            Proposals Sent
          </span>
        </div>
        <div className={`${baseClass}__stat`}>
          <div className={`${baseClass}__value-row`}>
            <span className={`${baseClass}__value`}>{stats?.replies ?? 0}</span>
            {changes && <ChangeIndicator change={changes.replies} />}
          </div>
          <span className={`${baseClass}__label`}>
            <HiOutlineChatBubbleLeftRight size={16} />
            Replies
          </span>
        </div>
        <div className={`${baseClass}__stat`}>
          <div className={`${baseClass}__value-row`}>
            <span className={`${baseClass}__value ${baseClass}__value--rate-${replyRateColor}`}>
              {formattedReplyRate}<span className={`${baseClass}__unit`}>%</span>
            </span>
            {changes && <ChangeIndicator change={changes.reply_rate} />}
          </div>
          <span className={`${baseClass}__label`}>
            <HiOutlineChartBar size={16} />
            Reply Rate
          </span>
        </div>
      </div>

      <div className={`${baseClass}__divider`} />

      <div className={`${baseClass}__secondary`}>
        <div className={`${baseClass}__stat--sm`}>
          <div className={`${baseClass}__value-row--sm`}>
            <span className={`${baseClass}__value--sm`}>{stats?.invites ?? 0}</span>
            {smChanges && <SmallChangeIndicator change={smChanges.invites} />}
          </div>
          <span className={`${baseClass}__label--sm`}>
            <HiOutlineEnvelope size={16} />
            Invites
          </span>
        </div>
        <div className={`${baseClass}__stat--sm`}>
          <div className={`${baseClass}__value-row--sm`}>
            <span className={`${baseClass}__value--sm`}>{stats?.offers_received ?? 0}</span>
            {smChanges && <SmallChangeIndicator change={smChanges.offers_received} />}
          </div>
          <span className={`${baseClass}__label--sm`}>
            <HiOutlineGift size={16} />
            Offers
          </span>
        </div>
        <div className={`${baseClass}__stat--sm`}>
          <div className={`${baseClass}__value-row--sm`}>
            <span className={`${baseClass}__value--sm`}>{stats?.direct_messages ?? 0}</span>
            {smChanges && <SmallChangeIndicator change={smChanges.direct_messages} />}
          </div>
          <span className={`${baseClass}__label--sm`}>
            <HiOutlineChatBubbleLeftEllipsis size={16} />
            Direct Messages
          </span>
        </div>
        <div className={`${baseClass}__stat--sm`}>
          <div className={`${baseClass}__value-row--sm`}>
            <span className={`${baseClass}__value--sm`}>{stats?.used_connects ?? 0}</span>
            {smChanges && <SmallChangeIndicator change={smChanges.used_connects} />}
          </div>
          <span className={`${baseClass}__label--sm`}>
            <HiOutlineBolt size={16} />
            Connects Used
            <Tooltip
              overlayClassName="stats-tooltip"
              title={
                <div className={`${baseClass}__tooltip`}>
                  <div className={`${baseClass}__tooltip-desc`}>Total connects spent on proposals, including base cost and boosts</div>
                  <div className={`${baseClass}__tooltip-formula`}>
                    <span>Includes:</span> Base bid cost + Boost connects
                  </div>
                </div>
              }
            >
              <HiOutlineInformationCircle size={16} />
            </Tooltip>
          </span>
        </div>
        <div className={`${baseClass}__stat--sm`}>
          <div className={`${baseClass}__value-row--sm`}>
            <span className={`${baseClass}__value--sm`}>{stats?.boost_connects ?? 0}</span>
            {smChanges && <SmallChangeIndicator change={smChanges.boost_connects} />}
          </div>
          <span className={`${baseClass}__label--sm`}>
            <HiOutlineArrowUpCircle size={16} />
            Boost Connects
            <Tooltip
              overlayClassName="stats-tooltip"
              title={
                <div className={`${baseClass}__tooltip`}>
                  <div className={`${baseClass}__tooltip-desc`}>Extra connects spent on boosting proposals to rank higher</div>
                  <div className={`${baseClass}__tooltip-formula`}>
                    <span>Formula:</span> Total Connects − Base Bid Cost
                  </div>
                </div>
              }
            >
              <HiOutlineInformationCircle size={16} />
            </Tooltip>
          </span>
        </div>
        <div className={`${baseClass}__stat--sm`}>
          <div className={`${baseClass}__value-row--sm`}>
            <span className={`${baseClass}__value--sm`}>{calculateAvgConnects(stats?.used_connects, stats?.bids)}</span>
            {smChanges && <SmallChangeIndicator change={smChanges.avg_connects} />}
          </div>
          <span className={`${baseClass}__label--sm`}>
            <HiOutlineLink size={16} />
            Avg. Connects per Bid
            <Tooltip
              overlayClassName="stats-tooltip"
              title={
                <div className={`${baseClass}__tooltip`}>
                  <div className={`${baseClass}__tooltip-desc`}>Average number of connects spent per proposal</div>
                  <div className={`${baseClass}__tooltip-formula`}>
                    <span>Formula:</span> Total Connects Used / Total Proposals Sent
                  </div>
                </div>
              }
            >
              <HiOutlineInformationCircle size={16} />
            </Tooltip>
          </span>
        </div>
        <div className={`${baseClass}__stat--sm`}>
          <div className={`${baseClass}__value-row--sm`}>
            <span className={`${baseClass}__value--sm`}>${calculateReplyCost(stats?.used_connects, stats?.replies)}</span>
            {smChanges && <SmallChangeIndicator change={smChanges.cost_per_reply} />}
          </div>
          <span className={`${baseClass}__label--sm`}>
            <HiOutlineCurrencyDollar size={16} />
            Cost per Reply
            <Tooltip
              overlayClassName="stats-tooltip"
              title={
                <div className={`${baseClass}__tooltip`}>
                  <div className={`${baseClass}__tooltip-desc`}>Estimated cost per reply based on buying 300 connects</div>
                  <div className={`${baseClass}__tooltip-formula`}>
                    <span>Formula:</span> Connects / 300 × $45 / Replies
                  </div>
                </div>
              }
            >
              <HiOutlineInformationCircle size={16} />
            </Tooltip>
          </span>
        </div>
        {isPipedriveConnected && (
          <div className={`${baseClass}__stat--sm`}>
            <div className={`${baseClass}__value-row--sm`}>
              <span className={`${baseClass}__value--sm`}>{stats?.sync_with_crms ?? 0}</span>
              {smChanges && <SmallChangeIndicator change={smChanges.sync_with_crms} />}
            </div>
            <span className={`${baseClass}__label--sm`}>
              <HiOutlineArrowPath size={16} />
              CRM Syncs
            </span>
          </div>
        )}
      </div>
    </div>
  );
};
